import React, { useMemo, useRef, useState } from 'react';
import { buildDefaultAuditOutputTemplate } from '../utils/auditOutputTemplateDefaults';
import { applyTemplateTokens } from '../utils/auditOutputTemplateData';
import {
  CERTIFICATE_FIELD_KEYS,
  normalizeCertificateCoordinateLayout,
} from '../utils/certificateCoordinateLayout';
import { pagePreviewStyle } from '../utils/auditOutputTemplatePdf';
import './AuditOutputTemplatePreview.css';

const MM_TO_PX = 3.78;

const tokenText = (value, runtimeData) => applyTemplateTokens(value, runtimeData);

const certificateFieldValue = (key, certificateConfig, runtimeData) => {
  if (key === 'certificateNumber') return runtimeData?.certificate?.certificateNumber || '-';
  if (key === 'datesBlock') {
    return (certificateConfig.dates || [])
      .map((item) => `${tokenText(item.label, runtimeData)}: ${tokenText(item.value, runtimeData)}`)
      .join('\n');
  }
  if (key === 'signatureBlock') {
    return [
      tokenText(certificateConfig.signatureName, runtimeData),
      tokenText(certificateConfig.signatureTitle, runtimeData),
    ].filter(Boolean).join('\n');
  }
  return tokenText(certificateConfig[key], runtimeData);
};

const AuditOutputTemplatePreview = ({
  template,
  runtimeData,
  editableLogo = false,
  onLogoChange,
  renderSitePage = false,
  editableCoordinates = false,
  onCoordinateFieldChange = null,
}) => {
  const normalizedTemplate = useMemo(() => buildDefaultAuditOutputTemplate(template), [template]);
  const containerRef = useRef(null);
  const dragStateRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const coordDragRef = useRef(null);
  const [selectedCoordField, setSelectedCoordField] = useState(null);

  const handlePointerDown = (event) => {
    if (!editableLogo || !containerRef.current || !normalizedTemplate.config.logo.enabled) return;
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: normalizedTemplate.config.logo.x,
      originY: normalizedTemplate.config.logo.y,
    };
    setDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragStateRef.current || !editableLogo || !containerRef.current || typeof onLogoChange !== 'function') return;
    const deltaX = (event.clientX - dragStateRef.current.startX) / MM_TO_PX;
    const deltaY = (event.clientY - dragStateRef.current.startY) / MM_TO_PX;
    onLogoChange({
      ...normalizedTemplate.config.logo,
      x: Math.max(0, dragStateRef.current.originX + deltaX),
      y: Math.max(0, dragStateRef.current.originY + deltaY),
    });
  };

  const stopDragging = () => {
    dragStateRef.current = null;
    setDragging(false);
  };

  const handleFieldPointerDown = (event, key) => {
    if (!editableCoordinates || typeof onCoordinateFieldChange !== 'function') return;
    const field = coordinateLayout.fields[key];
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    coordDragRef.current = { key, mode: 'move', startX: event.clientX, startY: event.clientY, originX: field.x, originY: field.y };
    setSelectedCoordField(key);
  };

  const handleResizePointerDown = (event, key) => {
    if (!editableCoordinates || typeof onCoordinateFieldChange !== 'function') return;
    const field = coordinateLayout.fields[key];
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    coordDragRef.current = { key, mode: 'resize', startX: event.clientX, startY: event.clientY, originWidth: field.width, originHeight: field.height };
    setSelectedCoordField(key);
  };

  const handleCoordPointerMove = (event) => {
    if (!coordDragRef.current || typeof onCoordinateFieldChange !== 'function') return;
    const { key, mode, startX, startY } = coordDragRef.current;
    const dx = (event.clientX - startX) / MM_TO_PX;
    const dy = (event.clientY - startY) / MM_TO_PX;
    if (mode === 'move') {
      onCoordinateFieldChange(key, { x: Math.max(0, coordDragRef.current.originX + dx), y: Math.max(0, coordDragRef.current.originY + dy) });
    } else {
      onCoordinateFieldChange(key, { width: Math.max(10, coordDragRef.current.originWidth + dx), height: Math.max(4, coordDragRef.current.originHeight + dy) });
    }
  };

  const stopCoordDragging = () => { coordDragRef.current = null; };

  const pageStyle = pagePreviewStyle(normalizedTemplate);
  const coordinateLayout = normalizeCertificateCoordinateLayout(normalizedTemplate.config.certificate.coordinateLayout);
  const sharedRuntime = {
    ...runtimeData,
    page: {
      current: renderSitePage ? 2 : 1,
      // A site appendix is only a second page when the certificate actually
      // contains multiple distinct sites. A single-site certificate must stay
      // one page even when a multi-site template is selected.
      total: normalizedTemplate.siteMode === 'multi-site' && (runtimeData?.certificate?.sites || []).length > 1 ? 2 : 1,
    },
  };

  return (
    <div
      ref={containerRef}
      className={`audit-output-preview-sheet${dragging ? ' is-dragging' : ''}`}
      style={pageStyle}
      onPointerMove={(e) => { handlePointerMove(e); handleCoordPointerMove(e); }}
      onPointerUp={() => { stopDragging(); stopCoordDragging(); }}
      onPointerCancel={() => { stopDragging(); stopCoordDragging(); }}
    >
      {normalizedTemplate.config.logo.enabled && normalizedTemplate.config.logo.dataUrl && (
        <img
          src={normalizedTemplate.config.logo.dataUrl}
          alt="Template logo"
          className={`audit-output-preview-logo${editableLogo ? ' is-editable' : ''}`}
          style={{
            left: `${normalizedTemplate.config.logo.x * MM_TO_PX}px`,
            top: `${normalizedTemplate.config.logo.y * MM_TO_PX}px`,
            width: `${normalizedTemplate.config.logo.width * MM_TO_PX}px`,
            height: `${normalizedTemplate.config.logo.height * MM_TO_PX}px`,
          }}
          onPointerDown={handlePointerDown}
        />
      )}

      {normalizedTemplate.config.header.enabled && (
        <header className={`audit-output-preview-header is-${normalizedTemplate.config.header.align}`}>
          <h2>{tokenText(normalizedTemplate.config.header.title, sharedRuntime)}</h2>
          <p>{tokenText(normalizedTemplate.config.header.subtitle, sharedRuntime)}</p>
        </header>
      )}

      {!renderSitePage && normalizedTemplate.outputType === 'certificate' && (
        <section className={`audit-output-preview-body${coordinateLayout.enabled ? ' is-coordinate-layout' : ''}`}>
          {coordinateLayout.enabled ? (
            <div className={`audit-output-coordinate-layer${editableCoordinates ? ' is-editable' : ''}`}>
              {CERTIFICATE_FIELD_KEYS.map(({ key, label }) => {
                const field = coordinateLayout.fields[key];
                const isSelected = selectedCoordField === key;
                return (
                  <div
                    key={key}
                    className={`audit-output-coordinate-field${editableCoordinates ? ' is-draggable' : ''}${isSelected ? ' is-selected' : ''}`}
                    style={{
                      left: `${field.x * MM_TO_PX}px`,
                      top: `${field.y * MM_TO_PX}px`,
                      width: `${field.width * MM_TO_PX}px`,
                      minHeight: `${field.height * MM_TO_PX}px`,
                      textAlign: field.align,
                      fontSize: `${field.fontSize}px`,
                    }}
                    title={label}
                    onPointerDown={editableCoordinates ? (e) => handleFieldPointerDown(e, key) : undefined}
                  >
                    {editableCoordinates && (
                      <span className="audit-output-coordinate-field-label">{label}</span>
                    )}
                    {certificateFieldValue(key, normalizedTemplate.config.certificate, sharedRuntime)}
                    {editableCoordinates && (
                      <div
                        className="audit-output-coordinate-field-resize"
                        onPointerDown={(e) => handleResizePointerDown(e, key)}
                      />
                    )}
                  </div>
                );
              })}
              {editableCoordinates && selectedCoordField && (() => {
                const sf = coordinateLayout.fields[selectedCoordField];
                const sfLabel = CERTIFICATE_FIELD_KEYS.find((f) => f.key === selectedCoordField)?.label || selectedCoordField;
                return (
                  <div className="audit-output-coord-toolbar" style={{ left: `${sf.x * MM_TO_PX}px`, top: `${Math.max(0, sf.y * MM_TO_PX - 38)}px` }}>
                    <span className="audit-output-coord-toolbar-label">{sfLabel}</span>
                    <div className="audit-output-coord-toolbar-sep" />
                    <button type="button" title="Smaller" onClick={() => onCoordinateFieldChange(selectedCoordField, { fontSize: Math.max(4, sf.fontSize - 1) })}>A−</button>
                    <span className="audit-output-coord-toolbar-size">{Math.round(sf.fontSize)}pt</span>
                    <button type="button" title="Larger" onClick={() => onCoordinateFieldChange(selectedCoordField, { fontSize: sf.fontSize + 1 })}>A+</button>
                    <div className="audit-output-coord-toolbar-sep" />
                    {['left', 'center', 'right'].map((a) => (
                      <button key={a} type="button" className={sf.align === a ? 'is-active' : ''} title={`Align ${a}`} onClick={() => onCoordinateFieldChange(selectedCoordField, { align: a })}>
                        {a === 'left' ? '⬅' : a === 'center' ? '↔' : '➡'}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          ) : null}
          {!coordinateLayout.enabled ? (
            <>
              <div className="audit-output-preview-title">{tokenText(normalizedTemplate.config.certificate.title, sharedRuntime)}</div>
              <div className="audit-output-preview-subtitle">{tokenText(normalizedTemplate.config.certificate.subtitle, sharedRuntime)}</div>
              {tokenText(normalizedTemplate.config.certificate.statement, sharedRuntime) ? (
                <p className="audit-output-preview-statement">
                  {tokenText(normalizedTemplate.config.certificate.statement, sharedRuntime)}
                </p>
              ) : null}
              <h3>{tokenText(normalizedTemplate.config.certificate.companyName, sharedRuntime)}</h3>
              <p>{tokenText(normalizedTemplate.config.certificate.companyAddress, sharedRuntime)}</p>

              <div className="audit-output-preview-meta">
                <div className="audit-output-preview-meta-card">
                  <span>{tokenText(normalizedTemplate.config.certificate.certificateNumberLabel, sharedRuntime)}</span>
                  <strong>{runtimeData?.certificate?.certificateNumber || '-'}</strong>
                </div>
                <div className="audit-output-preview-meta-card">
                  <span>{tokenText(normalizedTemplate.config.certificate.decisionLabel, sharedRuntime)}</span>
                  <strong>{runtimeData?.certificate?.certificationDecision || '-'}</strong>
                </div>
              </div>

              <div className="audit-output-preview-section">
                <label>Standards</label>
                <p>{tokenText(normalizedTemplate.config.certificate.standardsIntro, sharedRuntime)}</p>
                <strong>{tokenText(normalizedTemplate.config.certificate.standardsText, sharedRuntime)}</strong>
              </div>

              <div className="audit-output-preview-section">
                <label>{tokenText(normalizedTemplate.config.certificate.scopeLabel, sharedRuntime)}</label>
                <p>{tokenText(normalizedTemplate.config.certificate.scopeText, sharedRuntime)}</p>
              </div>

              <div className="audit-output-preview-section">
                <label>{tokenText(normalizedTemplate.config.certificate.verificationLabel, sharedRuntime)}</label>
                <p>{tokenText(normalizedTemplate.config.certificate.verificationText, sharedRuntime)}</p>
              </div>

              <div className="audit-output-preview-dates">
                {(normalizedTemplate.config.certificate.dates || []).map((item) => (
                  <div key={item.label} className="audit-output-preview-date-card">
                    <span>{tokenText(item.label, sharedRuntime)}</span>
                    <strong>{tokenText(item.value, sharedRuntime)}</strong>
                  </div>
                ))}
              </div>

              <div className="audit-output-preview-signature">
                {normalizedTemplate.config.certificate.signatureImage?.enabled && normalizedTemplate.config.certificate.signatureImage?.dataUrl ? (
                  <img
                    src={normalizedTemplate.config.certificate.signatureImage.dataUrl}
                    alt="Certificate signature"
                    className="audit-output-preview-signature-image"
                    style={{
                      width: `${normalizedTemplate.config.certificate.signatureImage.width * MM_TO_PX}px`,
                      height: `${normalizedTemplate.config.certificate.signatureImage.height * MM_TO_PX}px`,
                    }}
                  />
                ) : null}
                <strong>{tokenText(normalizedTemplate.config.certificate.signatureName, sharedRuntime)}</strong>
                <span>{tokenText(normalizedTemplate.config.certificate.signatureTitle, sharedRuntime)}</span>
              </div>

              {tokenText(normalizedTemplate.config.certificate.remarks, sharedRuntime) && (
                <div className="audit-output-preview-remarks">
                  {tokenText(normalizedTemplate.config.certificate.remarks, sharedRuntime)}
                </div>
              )}
            </>
          ) : null}
        </section>
      )}

      {!renderSitePage && normalizedTemplate.outputType === 'report' && (
        <section className="audit-output-preview-body">
          <div className="audit-output-preview-title">{tokenText(normalizedTemplate.config.report.title, sharedRuntime)}</div>
          <div className="audit-output-preview-subtitle">{tokenText(normalizedTemplate.config.report.subtitle, sharedRuntime)}</div>
          <p>{tokenText(normalizedTemplate.config.report.intro, sharedRuntime)}</p>
          <div className="audit-output-preview-section">
            <p>{tokenText(normalizedTemplate.config.report.body, sharedRuntime)}</p>
          </div>
          <div className="audit-output-preview-section">
            <label>{tokenText(normalizedTemplate.config.report.conclusionTitle, sharedRuntime)}</label>
            <p>{tokenText(normalizedTemplate.config.report.conclusionText, sharedRuntime)}</p>
          </div>
          <div className="audit-output-preview-section">
            <label>{tokenText(normalizedTemplate.config.report.recommendationTitle, sharedRuntime)}</label>
            <p>{tokenText(normalizedTemplate.config.report.recommendationText, sharedRuntime)}</p>
          </div>
        </section>
      )}

      {!renderSitePage && normalizedTemplate.outputType === 'opinion' && (
        <section className="audit-output-preview-body">
          <div className="audit-output-preview-title">{tokenText(normalizedTemplate.config.opinion.title, sharedRuntime)}</div>
          <div className="audit-output-preview-subtitle">{tokenText(normalizedTemplate.config.opinion.subtitle, sharedRuntime)}</div>
          <p>{tokenText(normalizedTemplate.config.opinion.intro, sharedRuntime)}</p>
          <div className="audit-output-preview-section">
            <label>{tokenText(normalizedTemplate.config.opinion.decisionLabel, sharedRuntime)}</label>
            <strong>{tokenText(normalizedTemplate.config.opinion.decisionValue, sharedRuntime)}</strong>
          </div>
          <div className="audit-output-preview-section">
            <p>{tokenText(normalizedTemplate.config.opinion.body, sharedRuntime)}</p>
          </div>
        </section>
      )}

      {renderSitePage && (
        <section className="audit-output-preview-body">
          <div className="audit-output-preview-title">{tokenText(normalizedTemplate.config.multiSite.title, sharedRuntime)}</div>
          <p>{tokenText(normalizedTemplate.config.multiSite.intro, sharedRuntime)}</p>
          <div className="audit-output-preview-site-list">
            {((normalizedTemplate.config.multiSite.includePrimarySiteOnAppendix !== false
              ? (runtimeData?.certificate?.sites || [])
              : (runtimeData?.certificate?.sites || []).slice(1)
            )).map((site, index) => (
              <div key={`${site.name}-${index}`} className="audit-output-preview-site-card">
                <strong>{index + 1}. {site.name}</strong>
                <span>{site.address}</span>
                <span>{site.scope}</span>
                {site.source === 'audit-scheduling' ? <small>Audit Scheduling Site</small> : null}
              </div>
            ))}
          </div>
          <p>{tokenText(normalizedTemplate.config.multiSite.footerNote, sharedRuntime)}</p>
        </section>
      )}

      {normalizedTemplate.config.footer.enabled && (
        <footer className="audit-output-preview-footer">
          <span>{tokenText(normalizedTemplate.config.footer.leftText, sharedRuntime)}</span>
          <span>{tokenText(normalizedTemplate.config.footer.rightText, sharedRuntime)}</span>
        </footer>
      )}
    </div>
  );
};

export default AuditOutputTemplatePreview;
